import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/utils/apiClient";
import { useAuth } from "@/state/auth";
import type { BrandVoicePayload } from "@/types/content";

export interface UpdateBrandVoicePayload {
  tone: string;
  do_say: string[];
  dont_say: string[];
  banned_terms: string[];
  signature_phrases: string[];
}

interface BrandVoiceEnvelope {
  config: BrandVoicePayload;
  defaults: BrandVoicePayload;
  updated_at?: string | null;
  updated_by?: string | null;
}

const fetchBrandVoice = async (): Promise<BrandVoicePayload> => {
  const envelope = await apiRequest<BrandVoiceEnvelope>("/api/admin/brand-voice");
  return {
    ...envelope.config,
    updated_at: envelope.updated_at ?? envelope.config.updated_at ?? null,
    updated_by: envelope.updated_by ?? envelope.config.updated_by ?? null,
  };
};

const saveBrandVoice = async (
  payload: UpdateBrandVoicePayload
): Promise<BrandVoicePayload> => {
  const { config } = await apiRequest<{ config: BrandVoicePayload }>(
    "/api/admin/brand-voice",
    { method: "PUT", body: JSON.stringify(payload) }
  );
  return config;
};

export const useBrandVoice = () => {
  const profile = useAuth((state) => state.profile);
  return useQuery({
    queryKey: ["brand-voice"],
    queryFn: fetchBrandVoice,
    staleTime: 60_000,
    enabled: Boolean(profile),
  });
};

export const useUpdateBrandVoice = () => {
  const profile = useAuth((state) => state.profile);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateBrandVoicePayload) => saveBrandVoice(payload),
    onSuccess: (config) => {
      if (!profile) return;
      queryClient.setQueryData(["brand-voice"], config);
      queryClient.invalidateQueries({ queryKey: ["brand-voice"] });
    },
  });
};
